import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";

// ============================================================
// CORN GROWTH SCROLL — Apple-style scroll-driven timelapse.
//
// Reproduce un timelapse fotorrealista del crecimiento de una
// planta de maíz, frame-by-frame, controlado por scroll.
//
// Video generado con Sora 2 (12s @ 30fps · fuente 1280x720),
// extraído cada 2do frame → 180 frames. Upscaled a 4K UHD (3840x2160)
// con LANCZOS progresivo + UnsharpMask + WebP q92 para máxima nitidez
// (v3-4k). Cache-bust via query param en FRAME_PATH.
//
// 3 actos narrativos sincronizados al scroll:
//   01 — SEMILLA      semilla amarilla emergiendo de tierra fértil
//   02 — CRECIMIENTO  brote, tallo, hojas
//   03 — COSECHA      planta vegetativa joven en campo dorado
// ============================================================

const TOTAL_FRAMES = 180;
// Cache-bust: cambio de versión al hacer re-encoding de frames
const FRAME_VERSION = "v4-clear";
const FRAME_PATH = (i) => `/corn-frames/frame_${String(i).padStart(4, "0")}.webp?v=${FRAME_VERSION}`;
// Aspect 16:9 — dimensiones REALES del source WebP (post-upscale 4K)
const VIDEO_W = 3840;
const VIDEO_H = 2160;

const DEFAULT_STAGES = [
  { at: 0.04, eyebrow: "01 — SEMILLA",     title: "Cada semilla",  subtitle: "guarda un código." },
  { at: 0.36, eyebrow: "02 — CRECIMIENTO", title: "Vida que sube",  subtitle: "fuerte y sana." },
  { at: 0.72, eyebrow: "03 — COSECHA",     title: "Producción",     subtitle: "garantizada y viva." },
];
const STAGE_AT = [0.04, 0.36, 0.72]; // umbrales de transición fijos
const DEFAULT_STATS = [
  { value: "+38%",  label: "Rendimiento" },
  { value: "99.4%", label: "Pureza" },
  { value: "GMO−",  label: "Verificado" },
];

export default function SeedDNAScroll({ cfg }) {
  // En móvil (viewport pequeño) NO cargamos los 48MB de frames.
  // Mostramos un banner estático equivalente con el primer frame como fondo.
  // Esto arregla el "carga y carga infinita" en conexiones móviles.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 1024
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Toggle de visibilidad desde el CMS
  if (cfg && cfg.enabled === false) return null;

  // Mezcla configuración del CMS con defaults
  const STAGES = (cfg && Array.isArray(cfg.stages) && cfg.stages.length === 3)
    ? cfg.stages.map((s, i) => ({ ...DEFAULT_STAGES[i], ...s, at: STAGE_AT[i] }))
    : DEFAULT_STAGES;
  const STATS = (cfg && Array.isArray(cfg.stats) && cfg.stats.length === 3)
    ? cfg.stats
    : DEFAULT_STATS;

  if (isMobile) return <SeedDNAMobileStatic STAGES={STAGES} STATS={STATS} />;

  return <SeedDNAScrollInner STAGES={STAGES} STATS={STATS} />;
}

// Versión estática para móvil / conexiones no aptas: hero visual con el
// primer y último frame (sólo 2 imágenes, ~500KB total) + el texto narrativo.
function SeedDNAMobileStatic({ STAGES, STATS }) {
  return (
    <section
      data-testid="seed-dna-mobile"
      className="relative w-full min-h-[100vh] overflow-hidden bg-[#0A0F0B]"
    >
      <img
        src={FRAME_PATH(TOTAL_FRAMES)}
        alt="Planta de maíz en cosecha"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F0B]/60 via-[#0A0F0B]/30 to-[#0A0F0B]" />
      <div className="relative z-10 flex flex-col justify-end min-h-[100vh] px-5 pb-16 pt-32 max-w-2xl">
        <span className="inline-flex items-center gap-2 rounded-full bg-black/45 border border-white/15 backdrop-blur-md px-4 py-1.5 self-start mb-6">
          <span className="w-2 h-2 rounded-full bg-agro-green shadow-[0_0_10px_rgba(2,191,68,0.9)] animate-pulse" />
          <span className="text-[10px] font-semibold tracking-[0.25em] text-white/90">
            AGROPORT · BIO-LAB
          </span>
        </span>
        <div className="text-[10px] font-bold tracking-[0.35em] text-agro-green mb-3">
          {STAGES[2].eyebrow}
        </div>
        <h2 className="text-4xl sm:text-5xl font-bold text-white leading-[1.05] tracking-tight drop-shadow-2xl">
          {STAGES[2].title}
        </h2>
        <p className="mt-3 text-xl sm:text-2xl font-light text-white/80 leading-[1.1] italic drop-shadow-lg">
          {STAGES[2].subtitle}
        </p>
        <div className="mt-8 grid grid-cols-3 gap-3">
          <Stat value={STATS[0].value} label={STATS[0].label} />
          <Stat value={STATS[1].value} label={STATS[1].label} />
          <Stat value={STATS[2].value} label={STATS[2].label} />
        </div>
      </div>
    </section>
  );
}

// Detecta conexión lenta / ahorro de datos para saltar la animación pesada.
// Regresa true si el navegador reporta 2g/3g o el usuario tiene "Save-Data" activo.
const isSlowConnection = () => {
  if (typeof navigator === "undefined") return false;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return false;
  if (c.saveData) return true;
  const t = (c.effectiveType || "").toLowerCase();
  return t === "slow-2g" || t === "2g" || t === "3g";
};

// Umbral: cuántos frames necesitamos cargados para mostrar la sección
// (el resto sigue cargando en background sin bloquear la UI).
const READY_THRESHOLD = 24;

function SeedDNAScrollInner({ STAGES, STATS }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const currentFrameRef = useRef(-1);
  const rafRef = useRef(null);
  const lowBandwidthRef = useRef(isSlowConnection());

  const [loaded, setLoaded] = useState(0);
  const [ready, setReady] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);

  // 1) PRECARGA progresiva
  // - Carga primero un lote inicial pequeño (READY_THRESHOLD frames) y libera la UI.
  // - Continúa cargando el resto en background con concurrencia baja.
  // - Si la conexión es lenta (2g/3g/saveData), carga sólo unos pocos frames clave y termina.
  useEffect(() => {
    let cancelled = false;
    let done = 0;
    const imgs = new Array(TOTAL_FRAMES);
    const slow = lowBandwidthRef.current;

    // Lista de frames a cargar. En modo lento, sólo cada 6º frame (30 frames ~8MB)
    // que da suficiente muestreo para percibir movimiento sin saturar.
    const framesToLoad = slow
      ? Array.from({ length: Math.ceil(TOTAL_FRAMES / 6) }, (_, k) => k * 6).filter((i) => i < TOTAL_FRAMES)
      : Array.from({ length: TOTAL_FRAMES }, (_, i) => i);

    const loadOne = (idx) => new Promise((resolve) => {
      const img = new Image();
      img.decoding = "async";
      img.src = FRAME_PATH(idx + 1);
      img.onload = img.onerror = () => {
        imgs[idx] = img;
        done += 1;
        if (!cancelled) {
          setLoaded(done);
          // Libera la UI en cuanto tengamos suficientes frames iniciales.
          if (done >= Math.min(READY_THRESHOLD, framesToLoad.length)) {
            imagesRef.current = imgs;
            setReady((prev) => prev || true);
          }
        }
        resolve();
      };
    });

    // Concurrencia baja para no saturar la red (evita "carga y carga").
    const CONCURRENCY = slow ? 2 : 4;
    let cursor = 0;
    const worker = async () => {
      while (!cancelled && cursor < framesToLoad.length) {
        const idx = framesToLoad[cursor++];
        await loadOne(idx);
      }
    };

    const start = async () => {
      const workers = Array.from({ length: CONCURRENCY }, () => worker());
      await Promise.all(workers);
      if (!cancelled) {
        imagesRef.current = imgs;
        setReady(true);
      }
    };
    start();

    // Timeout de seguridad: si a los 6s aún no se ha liberado la sección
    // (red muy lenta o frames bloqueados), liberamos con lo que haya para
    // no dejar al usuario con un spinner infinito ("carga y carga").
    const safetyTimer = setTimeout(() => {
      if (!cancelled) {
        imagesRef.current = imgs;
        setReady(true);
      }
    }, 6000);

    return () => { cancelled = true; clearTimeout(safetyTimer); };
  }, []);

  // 2) Setup canvas con devicePixelRatio
  useEffect(() => {
    if (!ready) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      currentFrameRef.current = -1;
      drawFrame(0);
    };
    setup();
    window.addEventListener("resize", setup);
    return () => window.removeEventListener("resize", setup);
  }, [ready]);

  // 3) Dibuja frame con "cover" (rellena todo el canvas, recorta sobrante)
  //    Si el frame pedido aún no está cargado (carga progresiva), busca el
  //    frame válido más cercano hacia atrás para evitar canvas en negro.
  const drawFrame = (frameIdx) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let img = imagesRef.current[frameIdx];
    if (!img) {
      // Busca hacia atrás el frame más cercano ya cargado
      for (let k = frameIdx - 1; k >= 0; k--) {
        if (imagesRef.current[k]) { img = imagesRef.current[k]; break; }
      }
      // Si aún no hay nada, busca hacia adelante
      if (!img) {
        for (let k = frameIdx + 1; k < imagesRef.current.length; k++) {
          if (imagesRef.current[k]) { img = imagesRef.current[k]; break; }
        }
      }
    }
    if (!img) return;
    const ctx = canvas.getContext("2d");
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    ctx.clearRect(0, 0, cssW, cssH);

    // "Cover": escala para llenar canvas conservando aspect, recorta exceso
    const scale = Math.max(cssW / VIDEO_W, cssH / VIDEO_H);
    const drawW = VIDEO_W * scale;
    const drawH = VIDEO_H * scale;
    const dx = (cssW - drawW) / 2;
    const dy = (cssH - drawH) / 2;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, dx, dy, drawW, drawH);
  };

  // 4) Scroll → frame
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    if (!ready) return;
    const clamped = Math.max(0, Math.min(1, p));
    const target = Math.min(TOTAL_FRAMES - 1, Math.floor(clamped * (TOTAL_FRAMES - 1)));

    let newStage = 0;
    for (let i = STAGES.length - 1; i >= 0; i--) {
      if (clamped >= STAGES[i].at) { newStage = i; break; }
    }
    if (newStage !== stageIdx) setStageIdx(newStage);

    if (target === currentFrameRef.current) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      drawFrame(target);
      currentFrameRef.current = target;
    });
  });

  const hudOpacity = useTransform(scrollYProgress, [0, 0.04, 0.95, 1], [0, 1, 1, 0]);
  const progressW = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);
  const scrollHintOp = useTransform(scrollYProgress, [0, 0.04], [1, 0]);
  const textCardOp = useTransform(scrollYProgress, [0, 0.05, 0.95, 1], [0, 1, 1, 0.6]);

  // Progreso relativo al umbral que libera la UI (no al total de 180 frames).
  // Así el usuario no ve "12% eternamente" si en conexión lenta no se cargan todos.
  const loadPct = Math.min(100, Math.round((loaded / READY_THRESHOLD) * 100));

  return (
    <section
      ref={containerRef}
      data-testid="seed-dna-scroll"
      className="relative w-full"
      style={{ height: "600vh" }}
    >
      <div className="sticky top-0 w-full h-screen overflow-hidden bg-[#0A0F0B]">
        {/* POSTER estático (último frame) para que no se vea negro
            durante la carga progresiva ni si el fetch falla. */}
        <img
          src={FRAME_PATH(1)}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-70"
          style={{ zIndex: 0 }}
        />
        {/* CANVAS FULLSCREEN — image-rendering: crisp para pixel-perfect */}
        <canvas
          ref={canvasRef}
          data-testid="seed-dna-canvas"
          className="absolute inset-0 w-full h-full"
          style={{ imageRendering: "auto", zIndex: 1 }}
        />

        {/* OVERLAY: viñeta MUY sutil solo para profundidad estética. Antes: 55% oscurecía toda la imagen. */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Viñeta sutil (10% en esquinas, mantiene detalle) */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_55%,rgba(0,0,0,0.15)_100%)]" />
          {/* Tinte inferior mínimo para separar del contenido siguiente */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-b from-transparent to-[#0A0F0B]/25" />
        </div>

        {/* LOADING OVERLAY */}
        {!ready && (
          <div
            className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#0A0F0B]/95 backdrop-blur-sm"
            data-testid="seed-dna-loading"
          >
            <div className="relative w-20 h-20 mb-7">
              <div className="absolute inset-0 rounded-full border-2 border-agro-green/20" />
              <div
                className="absolute inset-0 rounded-full border-2 border-transparent border-t-agro-green animate-spin"
                style={{ animationDuration: "1.2s" }}
              />
              <div className="absolute inset-0 flex items-center justify-center text-agro-green text-sm font-bold tracking-wider">
                {loadPct}%
              </div>
            </div>
            <div className="text-[10px] tracking-[0.35em] text-white/65 font-semibold mb-3">
              GERMINANDO
            </div>
            <div className="w-56 h-1 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-agro-green to-agro-yellow transition-all duration-200 ease-out"
                style={{ width: `${loadPct}%` }}
              />
            </div>
          </div>
        )}

        {/* HUD top-left */}
        <motion.div
          style={{ opacity: hudOpacity }}
          className="absolute top-6 md:top-24 left-4 md:left-10 z-20 flex items-center gap-3"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-black/45 border border-white/15 backdrop-blur-md px-4 py-1.5">
            <span className="w-2 h-2 rounded-full bg-agro-green shadow-[0_0_10px_rgba(2,191,68,0.9)] animate-pulse" />
            <span className="text-[10px] md:text-xs font-semibold tracking-[0.25em] text-white/90">
              AGROPORT · BIO-LAB
            </span>
          </span>
        </motion.div>

        {/* HUD top-right */}
        <motion.div
          style={{ opacity: hudOpacity }}
          className="absolute top-6 md:top-24 right-4 md:right-10 z-20 hidden sm:flex flex-col items-end gap-2"
        >
          <span className="text-[10px] tracking-[0.3em] text-white/70 font-semibold drop-shadow-md">
            ETAPA · {String(stageIdx + 1).padStart(2, "0")}/03
          </span>
          <div className="w-44 h-[2px] bg-white/15 overflow-hidden rounded-full">
            <motion.div
              style={{ width: progressW }}
              className="h-full bg-gradient-to-r from-agro-green to-agro-yellow"
            />
          </div>
        </motion.div>

        {/* TEXTO bottom-left con glassmorphism */}
        <motion.div
          style={{ opacity: textCardOp }}
          className="absolute bottom-16 md:bottom-20 left-4 md:left-10 z-20 max-w-xl"
        >
          {STAGES.map((s, i) => (
            <motion.div
              key={i}
              initial={false}
              animate={{
                opacity: stageIdx === i ? 1 : 0,
                y: stageIdx === i ? 0 : 16,
                filter: stageIdx === i ? "blur(0px)" : "blur(8px)",
              }}
              transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className={`${stageIdx === i ? "relative" : "absolute inset-0"}`}
            >
              <div
                className="text-[10px] md:text-xs font-bold tracking-[0.35em] text-agro-green mb-3 md:mb-4 drop-shadow-md"
                data-testid={`seed-stage-${i}-eyebrow`}
              >
                {s.eyebrow}
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-[1.05] tracking-tight drop-shadow-2xl">
                {s.title}
              </h2>
              <p className="mt-2 md:mt-3 text-xl sm:text-2xl lg:text-4xl font-light text-white/75 leading-[1.1] italic drop-shadow-lg">
                {s.subtitle}
              </p>
            </motion.div>
          ))}

          {/* Stats últimas etapas */}
          <motion.div
            initial={false}
            animate={{ opacity: stageIdx === STAGES.length - 1 ? 1 : 0, y: stageIdx === STAGES.length - 1 ? 0 : 20 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 md:mt-8 grid grid-cols-3 gap-3 md:gap-4 max-w-sm"
          >
            <Stat value={STATS[0].value} label={STATS[0].label} />
            <Stat value={STATS[1].value} label={STATS[1].label} />
            <Stat value={STATS[2].value} label={STATS[2].label} />
          </motion.div>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          style={{ opacity: scrollHintOp }}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
        >
          <span className="text-[10px] tracking-[0.3em] text-white/70 font-semibold drop-shadow">
            DESLIZA Y MIRA CRECER
          </span>
          <div className="w-px h-8 bg-gradient-to-b from-agro-green to-transparent" />
        </motion.div>
      </div>
    </section>
  );
}

function Stat({ value, label }) {
  return (
    <div className="border-l-2 border-agro-green/70 pl-3 bg-black/30 backdrop-blur-sm rounded-r-lg py-1.5 pr-2">
      <div className="text-lg md:text-2xl font-bold text-white tabular-nums">{value}</div>
      <div className="text-[9px] md:text-[10px] uppercase tracking-[0.2em] text-white/65 mt-0.5">
        {label}
      </div>
    </div>
  );
}
